/**
 * API Configuration
 *
 * Central configuration for API endpoints and base URL.
 * Base URL is /api/v1 — individual endpoints should NOT include it.
 */

import { currentDevMode, isLocalBackend } from '@/config/devMode';

const LOCAL_API = 'http://localhost:3000/api/v1';
const REMOTE_API = 'https://api.media-vault.eu/api/v1';

function resolveApiBaseUrl(): string {
  const envOverride = import.meta.env.VITE_API_BASE_URL;
  if (envOverride) return envOverride;
  return isLocalBackend(currentDevMode()) ? LOCAL_API : REMOTE_API;
}

export const API_BASE_URL = resolveApiBaseUrl();

/**
 * Convert HTTP URL to WebSocket URL
 *
 * Replaces http/https protocol with ws/wss, and removes /api/v1 suffix
 *
 * @param httpUrl - HTTP URL (e.g., 'http://localhost:3000/api/v1')
 * @returns WebSocket URL (e.g., 'ws://localhost:3000/cable')
 */
export function httpToWebSocketUrl(httpUrl: string): string {
  // Replace protocol (https: -> wss:, http: -> ws:) and path (/api/v1 -> /cable)
  const protocol = httpUrl.startsWith('https:') ? 'wss:' : 'ws:';
  const wsUrl = protocol + httpUrl.substring(httpUrl.indexOf('//'));
  return wsUrl.replace(/\/api\/v1$/, '/cable');
}

/**
 * Get WebSocket URL for ActionCable connection
 *
 * Derives from HTTP base URL by replacing the protocol with ws/wss.
 * For use with Rails ActionCable connections.
 *
 * @returns WebSocket URL (e.g., 'ws://localhost:3000/cable' or 'wss://example.com/cable')
 */
export function getWebSocketUrl(): string {
  return httpToWebSocketUrl(API_BASE_URL);
}
